import { Container } from "../components/Container";
import { MentionCard } from "../components/MentionCard";
import { SectionHeading } from "../components/SectionHeading";
import styles from "./HonorableMentions.module.css";
import type { BoardEntry, BoardResponse } from "../api/types";
import type { Resource } from "../hooks/useApiResource";

/**
 * Honorable Mentions — paid one-day listings that are **outside** the Top 10 (canon § 10).
 *
 * They come from the same board response as the Top 10, so this section is a second view of
 * data already loaded rather than a second request. It is built to look unmistakably
 * separate: its own region and heading below the board, no rank column, no brass, and cards
 * that carry none of the board row's ranked furniture. Canon § 10 says a mention never
 * becomes a Top 10 position, and the layout must not leave that to the copy alone.
 *
 * `WEB-026` restored the design canvas's **card grid** after `WEB-010` had made these
 * one-line rows. A card separates by shape — it is not row-shaped, so it cannot be read as a
 * board row — where a row could only separate by absence. See `src/components/MentionCard.tsx`.
 *
 * `WEB-010` cut the section's explanatory paragraph, and `WEB-026` did not bring it back —
 * the canvas's own line is refused, not restored. It reads "Bids that didn't clear today's
 * Top 10. Unranked, listed alphabetically.", and canon § 10 makes a mention a *purchased*
 * one-day listing that never entered the auction, so the first sentence describes something
 * that does not exist. Canon outranks a drawing (`AGENTS.md` § 3). What is left is the
 * shortest true statement of what these are — that they were paid for, and that they are outside the Top
 * 10 — because canon § 12 requires paid placement to be *described* as paid placement, and
 * before this task that paragraph was the only place on the page saying so. The prose that
 * went with it is not missed. It was also wrong: it opened "Bids that didn't clear today's
 * Top 10", which describes a losing bidder, and canon § 10 makes a mention a purchased
 * listing that is not in the auction at all.
 *
 * The mentions are sorted alphabetically. Core returns them in its own order, and any order
 * a reader can perceive as a ranking will be read as one — alphabetical is the order that
 * makes no claim. It is applied here rather than asked of core because it is a presentation
 * decision: nothing downstream depends on it.
 *
 * The list is **unbounded** (canon `D-007`): every mention core returns for the display day
 * is shown. Canon § 10 sells a mention as a one-day listing, so silently showing a sample of
 * the day's mentions would be selling something the page does not deliver.
 *
 * The section is absent when there are none. An empty region under the board would only
 * invite the reader to wonder whether a mention had been hidden from them.
 */
export function HonorableMentions({ resource }: { resource: Resource<BoardResponse> }) {
  if (resource.state.status !== "ready") return null;
  const board = resource.state.data;
  if (board.honorableMentions.length === 0) return null;

  const alphabetical = [...board.honorableMentions].sort(byProductName);

  return (
    <section className={styles.section} aria-labelledby="honorable">
      <Container>
        <SectionHeading
          id="honorable"
          title="Honorable Mentions"
          meta="Paid one-day listings, outside the Top 10"
        />
        <ul className={styles.mentions} role="list">
          {alphabetical.map((entry) => (
            <li key={entry.deal.id}>
              <MentionCard entry={entry} />
            </li>
          ))}
        </ul>
      </Container>
    </section>
  );
}

/** Case-insensitive and locale-aware, so "acme" and "Ábaco" sort where a reader expects. */
function byProductName(left: BoardEntry, right: BoardEntry): number {
  return left.deal.productName.localeCompare(right.deal.productName, undefined, {
    sensitivity: "base",
  });
}
